import React, { useEffect, useCallback } from 'react';
import { Alert, Box } from '@mui/material';
import { DashboardHeader } from './components/DashboardHeader';
import { TabPanel } from './components/TabPanel';
import { LoadingState } from './components/LoadingState';
import { useDashboardStore } from '../../stores/dashboardStore';
import { useAuthV2 } from '../../contexts/AuthContextV2';
import { MainWrapper, MainContainer, TopSectionWrapper } from './Dashboard.styles';
import { trackPageView } from '../../utils/analytics';
import ChatWidget from '../ChatWidget/ChatWidget';

// Minimal logging

const Dashboard: React.FC = () => {
  // Get auth context
  const auth = useAuthV2();

  // Get store state and actions
  const {
    // State
    selectedSerial,
    sliderValues,
    fileFilters,
    hasAttemptedLoad,
    activeTab,
    isLoggingOut,
    data,
    loading,
    updating,
    error,

    // Actions
    setActiveTab,
    updateFilters,
    resetFileFilters,
    signOut: storeSignOut,
    resetLogoutState,
    loadInitialData,
    refreshOverviewData,
    loadDroneData,
    resetDroneData,
    initializeFromUrl,
    handlePopState,
    handleSliderEffects,
  } = useDashboardStore();

  // Create proper signOut handler
  const handleSignOut = useCallback(async () => {
    storeSignOut(); // Set loading state in store
    try {
      // 'local' scope: don't revoke every session for this user — a browser
      // sign-out shouldn't kill their xer-knowledge MCP connector (#1348).
      await auth.signOut('local');
    } catch (error) {
      console.error('🔘 Dashboard: Sign out failed:', error);
    }
  }, [auth, storeSignOut]);

  // Reset isLoggingOut when user logs back in
  useEffect(() => {
    if (auth.isAuthenticated && isLoggingOut) {
      resetLogoutState();
    }
  }, [auth.isAuthenticated, isLoggingOut, resetLogoutState]);

  // THIS IS WHERE WE CLEAR CACHE ON LOGIN
  // Clear all caches whenever user becomes authenticated (on every login)
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      resetDroneData();
    }
  }, [auth.isAuthenticated, auth.user, resetDroneData]);

  // Initialize from URL parameters on mount and listen for back/forward navigation
  useEffect(() => {
    initializeFromUrl();

    const onPopState = () => handlePopState();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [initializeFromUrl, handlePopState]);

  // Fire GA4 page_view per tab. Covers initial mount, user clicks, and popstate
  // since all three update activeTab in the store.
  useEffect(() => {
    if (auth.isAuthenticated && activeTab) {
      trackPageView(`/${activeTab}`);
    }
  }, [activeTab, auth.isAuthenticated]);

  // Load initial data when auth is ready
  useEffect(() => {
    if (!hasAttemptedLoad && auth.isAuthenticated) {
      loadInitialData();
    }
  }, [loadInitialData, hasAttemptedLoad, auth.isAuthenticated]);

  // Handle drone data loading based on serial
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    if (!selectedSerial) {
      resetDroneData();
      return;
    }

    loadDroneData(selectedSerial);
  }, [selectedSerial, loadDroneData, resetDroneData, auth.isAuthenticated]);

  // Handle slider effects with debouncing
  useEffect(() => {
    const cleanup = handleSliderEffects();
    return cleanup;
  }, [sliderValues, handleSliderEffects]);

  const flightDates = data.dateRange?.flight_dates || [];

  // Show loading state for initial load
  if (loading && !hasAttemptedLoad) {
    return <LoadingState />;
  }

  // Show error state
  if (error && !hasAttemptedLoad) {
    return <LoadingState error={error} />;
  }

  return (
    <MainWrapper>
      {/* Header */}
      <DashboardHeader
        user={auth.user}
        profile={auth.profile}
        onSignOut={handleSignOut}
        isLoggingOut={isLoggingOut}
      />

      {/* Main Content */}
      <MainContainer maxWidth="xl">
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* minWidth:0 lets this flex item shrink below its content's
              min-content width. Without it, an unbreakable token anywhere in a
              tab (e.g. a long note) forces the column wider than the viewport
              and the whole page gains horizontal scroll. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TopSectionWrapper>
              {/* Tab Panel - Always show as Files tab doesn't require drone selection */}
              <TabPanel
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onFilterChange={updateFilters}
                onResetFileFilters={resetFileFilters}
                data={data}
                filters={{
                  selectedSerial,
                  sliderValues,
                  fileFilters,
                }}
                flightDates={flightDates}
                isUpdating={updating}
                serialNumbers={data.serialNumbers}
                profile={auth.profile}
                onRefreshOverview={refreshOverviewData}
              />
            </TopSectionWrapper>
          </Box>
        </Box>
      </MainContainer>

      {/* Floating Chat Widget */}
      <ChatWidget />
    </MainWrapper>
  );
};

export default Dashboard;
